import { useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Alert, Button, Card, CardBody, CardHeader, Collapse, Spinner, Table } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import { toast } from "react-toastify";
import { getResidentRecord, runRenewalSuggestion } from "../api";
import { canSuggest, verdictTone, money, hoursLabel, newInspectionUrl, tiles } from "./residentRecord";
import "./residentRecord.scss";

const dash = (v) => (v == null || v === "" ? "—" : String(v));
const yn = (v) => (v == null ? "—" : v ? "Yes" : "No");

export const ResidentRecord = ({ itemId, propertyId, buildingName, unitNo, userRole, userLead }) => {
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [rec, setRec] = useState(null);
  const [running, setRunning] = useState(false);
  const [showEarlier, setShowEarlier] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getResidentRecord(itemId);
      setRec(res.data);
      setLoaded(true);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !loaded && !loading) load();
  };

  const suggest = async () => {
    setRunning(true);
    try {
      const res = await runRenewalSuggestion(itemId);
      setRec((r) => ({ ...r, suggestion: res.data }));
    } catch (e) {
      toast.error(e?.response?.data?.message || "Couldn't generate a renewal suggestion");
    } finally {
      setRunning(false);
    }
  };

  const scrollTo = (block) => {
    const el = document.getElementById(`rr-${block}`);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const unitUrl = newInspectionUrl({ propertyId, buildingName, unitNo });
  const warned = (k) => !!rec?.warnings?.includes(k);
  const failed = (label) => <div className="text-muted small">Couldn't load {label}</div>;

  const tickets = rec?.tickets || [];
  const current = tickets.filter((t) => !t.before_tenancy);
  const earlier = tickets.filter((t) => t.before_tenancy);
  const shownTickets = showEarlier ? tickets : current;
  const sug = rec?.suggestion;

  return (
    <Card className="rr-card">
      <CardHeader className="d-flex justify-content-between align-items-center" style={{ cursor: "pointer" }} onClick={toggle}>
        <h5 className="mb-0">Resident record</h5>
        <div className="d-flex align-items-center gap-2">
          {loading && rec && <Spinner size="sm" />}
          {open && (
            <Button
              color="link"
              size="sm"
              className="p-0"
              title="Refresh"
              type="button"
              disabled={loading}
              onClick={(e) => { e.stopPropagation(); load(); }}
            >
              <FeatherIcon icon="refresh-cw" size={16} />
            </Button>
          )}
          <FeatherIcon icon={open ? "chevron-up" : "chevron-down"} size={18} />
        </div>
      </CardHeader>
      <Collapse isOpen={open}>
        <CardBody>
          {loading && !rec && <div className="text-center py-3"><Spinner size="sm" /></div>}
          {error && !loading && (
            <Alert color="warning">
              Couldn't load the resident record ·{" "}
              <Button color="link" size="sm" type="button" className="p-0 align-baseline" onClick={load}>Retry</Button>
            </Alert>
          )}
          {rec && (
            <>
              {rec.warnings?.length > 0 && (
                <Alert color="warning" className="py-2 small">
                  Some parts couldn't load: {rec.warnings.join(", ")} — counts may be incomplete.
                </Alert>
              )}
              <div className="rr-tiles">
                {tiles(rec).map((t) => (
                  <button type="button" key={t.key} className={`rr-tile ${t.tone === "bad" ? "is-bad" : ""}`} onClick={() => scrollTo(t.block)}>
                    <div className="rr-tile__label">{t.label}</div>
                    <div className="rr-tile__value">{t.key === "incentives" && warned("nsf") ? "Couldn't load" : t.value}</div>
                  </button>
                ))}
              </div>

              <section id="rr-inspections" className="rr-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Suite inspections</h6>
                  <Button size="sm" color="primary" outline type="button" onClick={() => history.push(unitUrl)}>New inspection</Button>
                </div>
                {warned("inspections") ? failed("inspections") : rec.inspections.length === 0 ? (
                  <div className="text-muted small">No inspections recorded</div>
                ) : (
                  <Table size="sm" responsive>
                    <thead><tr><th>Date</th><th>Type</th><th>Overall</th><th>Cleanliness</th><th>Damages</th><th>Smoking</th><th>Inspector</th><th /></tr></thead>
                    <tbody>
                      {rec.inspections.map((i) => (
                        <tr key={i.id} className={i.previous_resident ? "rr-prev" : ""}>
                          <td>
                            {dash(i.date)}
                            {i.is_draft && <span className="rr-badge">Draft</span>}
                            {i.previous_resident && <span className="rr-badge">previous resident</span>}
                          </td>
                          <td>{dash(i.type)}</td>
                          <td>{dash(i.overall_status)}</td>
                          <td>{dash(i.cleanliness)}</td>
                          <td>{yn(i.any_damages)}</td>
                          <td>{yn(i.signs_of_smoking)}</td>
                          <td>{dash(i.inspector)}</td>
                          <td><Button size="sm" color="link" type="button" className="p-0" onClick={() => window.open(unitUrl, "_blank")}>Open</Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </section>

              <section id="rr-tickets" className="rr-section">
                <h6>Maintenance</h6>
                {warned("tickets") ? failed("maintenance") : tickets.length === 0 ? (
                  <div className="text-muted small">No maintenance requests</div>
                ) : (
                  <>
                    <Table size="sm" responsive>
                      <thead><tr><th>Date</th><th>Request</th><th>Status</th><th>Time to close</th><th /><th /></tr></thead>
                      <tbody>
                        {shownTickets.map((t) => (
                          <tr key={t.id} className={t.before_tenancy ? "rr-prev" : ""}>
                            <td>{dash(t.date)}</td>
                            <td>{dash(t.request_type)}</td>
                            <td>{dash(t.status)}</td>
                            <td>{hoursLabel(t.hours_to_close)}</td>
                            <td>{t.repeat && <span className="rr-pill rr-pill--soon">Repeat</span>}</td>
                            <td><Button size="sm" color="link" type="button" className="p-0" onClick={() => window.open("/tickets_list", "_blank")}>Open</Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    {earlier.length > 0 && (
                      <Button size="sm" color="link" type="button" className="p-0" onClick={() => setShowEarlier((v) => !v)}>
                        {showEarlier ? "Hide earlier" : `Show earlier (${earlier.length})`}
                      </Button>
                    )}
                  </>
                )}
              </section>

              <section id="rr-nsf" className="rr-section">
                <h6>NSF</h6>
                {warned("nsf") ? failed("NSF") : rec.nsf.length === 0 ? (
                  <div className="text-muted small">No NSF recorded</div>
                ) : (
                  <ul className="mb-0">
                    {rec.nsf.map((n) => (
                      <li key={n.pad_id || `${n.date}-${n.amount}`}>
                        {dash(n.date)} · {money(n.amount)}{n.note ? ` · ${n.note}` : ""}{n.by ? ` · ${n.by}` : ""}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section id="rr-suggestion" className="rr-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Renewal suggestion</h6>
                  {canSuggest(userRole, userLead) && (
                    <Button size="sm" color="primary" type="button" disabled={running} onClick={suggest}>
                      {running && <Spinner size="sm" className="me-2" />}
                      Suggest renewal
                    </Button>
                  )}
                </div>
                {sug ? (
                  <>
                    <span className={`rr-pill rr-pill--${verdictTone(sug.verdict)}`}>{sug.verdict}</span>
                    <ul className="mt-2 mb-1">{(sug.reasons || []).map((r, idx) => <li key={idx}>{r}</li>)}</ul>
                    <div className="text-muted small">as of {dash(sug.as_of)}{sug.by?.name ? ` by ${sug.by.name}` : ""}</div>
                  </>
                ) : (
                  <div className="text-muted small">No suggestion yet</div>
                )}
              </section>

              <div id="rr-incentives" />
              <div id="rr-addendums" />
              <div id="rr-tenancy" />
            </>
          )}
        </CardBody>
      </Collapse>
    </Card>
  );
};
